import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BottomNav } from "@/components/BottomNav";
import { Plane, Building2, FileText, Compass, UtensilsCrossed, HelpCircle, LifeBuoy, ShieldAlert, ChevronRight, HandHeart, BookOpen, HeartHandshake } from "lucide-react";

const ITEMS = [
  { id: "travel", icon: Plane, to: "/section/travel" },
  { id: "hotels", icon: Building2, to: "/section/hotels" },
  { id: "documents", icon: FileText, to: "/section/documents" },
  { id: "umrah", icon: Compass, to: "/section/umrah" },
  { id: "quran", icon: BookOpen, to: "/section/quran" },
  { id: "dua", icon: HeartHandshake, to: "/section/dua" },
  { id: "janazah", icon: HandHeart, to: "/section/janazah" },
  { id: "food", icon: UtensilsCrossed, to: "/section/food" },
  { id: "faq", icon: HelpCircle, to: "/section/faq" },
  { id: "support", icon: LifeBuoy, to: "/section/support" },
];

export default function MorePage() {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <div className="min-h-[100svh] bg-[#F7F4EE]" data-testid="more-page">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/92 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between px-5 py-3 sm:px-6">
          <img src="/maqam-logo.png" alt="Maqam" className="h-7 w-auto" />
          <LanguageSwitcher variant="dark" />
        </div>
      </header>

      <div className="mx-auto w-full max-w-xl px-5 pb-40 pt-8 sm:px-6">
        <div className="mb-6 animate-fade-up">
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A192F]">{t("more.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("more.subtitle")}</p>
        </div>

        <div className="stagger space-y-3">
          {ITEMS.map(({ id, icon: Icon, to }) => (
            <button
              key={id}
              onClick={() => navigate(to)}
              data-testid={`more-item-${id}`}
              className="group maqam-card flex w-full items-center gap-4 p-5 text-left transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.99]"
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#0A192F] text-[#E9CE7D]">
                <Icon size={21} strokeWidth={1.6} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-base font-bold text-[#0A192F]">{t(`sec.${id}.title`)}</p>
                <p className="truncate text-xs text-slate-500">{t(`sec.${id}.desc`)}</p>
              </div>
              <ChevronRight size={18} className="text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#D4AF37]" />
            </button>
          ))}

          {/* Emergency SOS */}
          <button
            onClick={() => navigate("/sos")}
            data-testid="more-item-sos"
            className="group flex w-full items-center gap-4 rounded-[1.5rem] border border-[#B42318]/20 bg-[#FDECEA] p-5 text-left transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.99]"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#B42318] text-white">
              <ShieldAlert size={21} strokeWidth={1.7} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-base font-bold text-[#B42318]">{t("sos.title")}</p>
              <p className="truncate text-xs text-[#B42318]/70">{t("sos.hold")}</p>
            </div>
            <ChevronRight size={18} className="text-[#B42318]/50 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
